import { apiFetch, useMockApi } from '@/lib/api/client'
import type { ScanSettings } from '@/lib/api/settings'

export interface ScanResultSummary {
  totalFound: number
  totalAdded: number
  totalUpdated: number
  totalArchived: number
  categoriesProcessed: number
}

export interface ScanRunListItem {
  id: string
  startedAt: string
  finishedAt?: string
  status: string
  createdBy: string
  categoriesCount: number
  resultSummary?: ScanResultSummary
}

export interface ScanRunItem {
  id: string
  modelId?: string
  categoryId: string
  action: string
  reason?: string
}

export interface ScanRunDetails {
  id: string
  startedAt: string
  finishedAt?: string
  status: string
  createdBy: string
  resultSummary?: ScanResultSummary
  warnings: Array<{ categoryId?: string; message: string }>
  items: ScanRunItem[]
}

export interface TriggerScanRequest {
  categoryIds: string[]
  scanTargetModelsPerCategory: number
  maxActiveModelsPerCategory: number
  priceFilters?: {
    freeOnly?: boolean
    maxInputPrice?: number
    maxOutputPrice?: number
  }
}

export interface TriggerScanResult {
  scanRunId: string
  status: string
  startedAt: string
}

export interface ScansApi {
  triggerScan(request: TriggerScanRequest): Promise<TriggerScanResult>
  listScans(page?: number, perPage?: number): Promise<{ items: ScanRunListItem[]; total: number }>
  getScan(scanRunId: string): Promise<ScanRunDetails>
}

function mapSummary(body?: Record<string, unknown>): ScanResultSummary | undefined {
  if (!body) return undefined
  return {
    totalFound: Number(body.total_found ?? 0),
    totalAdded: Number(body.total_added ?? 0),
    totalUpdated: Number(body.total_updated ?? 0),
    totalArchived: Number(body.total_archived ?? 0),
    categoriesProcessed: Number(body.categories_processed ?? 0),
  }
}

function mapListItem(body: Record<string, unknown>): ScanRunListItem {
  return {
    id: String(body.id ?? ''),
    startedAt: String(body.started_at ?? ''),
    finishedAt: body.finished_at ? String(body.finished_at) : undefined,
    status: String(body.status ?? ''),
    createdBy: String(body.created_by ?? ''),
    categoriesCount: Number(body.categories_count ?? 0),
    resultSummary: mapSummary(body.result_summary as Record<string, unknown> | undefined),
  }
}

function mapDetails(body: Record<string, unknown>): ScanRunDetails {
  const warnings = (body.warnings as Array<Record<string, unknown>> | undefined) ?? []
  const items = (body.items as Array<Record<string, unknown>> | undefined) ?? []

  return {
    id: String(body.id ?? ''),
    startedAt: String(body.started_at ?? ''),
    finishedAt: body.finished_at ? String(body.finished_at) : undefined,
    status: String(body.status ?? ''),
    createdBy: String(body.created_by ?? ''),
    resultSummary: mapSummary(body.result_summary as Record<string, unknown> | undefined),
    warnings: warnings.map((warning) => ({
      categoryId: warning.category_id ? String(warning.category_id) : undefined,
      message: String(warning.message ?? ''),
    })),
    items: items.map((item) => ({
      id: String(item.id ?? ''),
      modelId: item.model_id ? String(item.model_id) : undefined,
      categoryId: String(item.category_id ?? ''),
      action: String(item.action ?? ''),
      reason: item.reason ? String(item.reason) : undefined,
    })),
  }
}

class MockScansApi implements ScansApi {
  private runs: ScanRunListItem[] = [
    {
      id: 'scan-demo-1',
      startedAt: new Date().toISOString(),
      finishedAt: new Date().toISOString(),
      status: 'completed',
      createdBy: 'admin@local',
      categoriesCount: 3,
      resultSummary: {
        totalFound: 12,
        totalAdded: 4,
        totalUpdated: 0,
        totalArchived: 1,
        categoriesProcessed: 3,
      },
    },
  ]

  async triggerScan(_request: TriggerScanRequest): Promise<TriggerScanResult> {
    return {
      scanRunId: `scan-${crypto.randomUUID()}`,
      status: 'running',
      startedAt: new Date().toISOString(),
    }
  }

  async listScans(): Promise<{ items: ScanRunListItem[]; total: number }> {
    return { items: structuredClone(this.runs), total: this.runs.length }
  }

  async getScan(scanRunId: string): Promise<ScanRunDetails> {
    const run = this.runs.find((item) => item.id === scanRunId) ?? this.runs[0]
    return {
      ...run,
      warnings: [],
      items: [
        {
          id: 'item-1',
          categoryId: 'cat-1',
          action: 'added_new',
          reason: 'matches_coding_rules',
        },
      ],
    }
  }
}

class HttpScansApi implements ScansApi {
  async triggerScan(request: TriggerScanRequest): Promise<TriggerScanResult> {
    const body = await apiFetch<{
      scan_run_id: string
      status: string
      started_at: string
    }>('/api/v1/scans/trigger', {
      method: 'POST',
      body: JSON.stringify({
        category_ids: request.categoryIds,
        scan_target_models_per_category: request.scanTargetModelsPerCategory,
        max_active_models_per_category: request.maxActiveModelsPerCategory,
        price_filters: request.priceFilters
          ? {
              free_only: request.priceFilters.freeOnly ?? false,
              max_input_price: request.priceFilters.maxInputPrice,
              max_output_price: request.priceFilters.maxOutputPrice,
            }
          : undefined,
      }),
    })

    return {
      scanRunId: body.scan_run_id,
      status: body.status,
      startedAt: body.started_at,
    }
  }

  async listScans(page = 1, perPage = 20): Promise<{ items: ScanRunListItem[]; total: number }> {
    const body = await apiFetch<{
      items: Array<Record<string, unknown>>
      total: number
    }>(`/api/v1/scans?page=${page}&per_page=${perPage}`)

    return {
      items: body.items.map(mapListItem),
      total: body.total,
    }
  }

  async getScan(scanRunId: string): Promise<ScanRunDetails> {
    const body = await apiFetch<Record<string, unknown>>(`/api/v1/scans/${scanRunId}`)
    return mapDetails(body)
  }
}

export const scansApi: ScansApi = useMockApi() ? new MockScansApi() : new HttpScansApi()

export function buildTriggerScanRequest(
  categoryIds: string[],
  scanSettings: ScanSettings,
): TriggerScanRequest {
  return {
    categoryIds,
    scanTargetModelsPerCategory: scanSettings.targetModelsPerCategory,
    maxActiveModelsPerCategory: scanSettings.maxActivePerCategory,
    priceFilters: {
      freeOnly: scanSettings.freeOnly,
      maxInputPrice: scanSettings.maxInputPrice,
      maxOutputPrice: scanSettings.maxOutputPrice,
    },
  }
}
